/**
 * pulsebot/capabilities.ts — the SINGLE SOURCE OF TRUTH for "what can PulseBot do".
 *
 * This registry powers the `help` intent (handlers.ts → helpCapabilities) so the
 * bot can describe itself without anyone hand-maintaining a second list. The
 * greeting points users here instead of hardcoding an ever-growing phrase list.
 *
 * ★ WHEN YOU ADD A NEW EXECUTOR OR READ HANDLER: add ONE entry here. That's the
 *   only place help text lives — the help reply, grouping, and discoverability
 *   all derive from this array. (Keyword matching itself still lives in
 *   router.ts INTENTS for reads and dialog.ts triggers for writes; this file is
 *   the human-facing capability catalogue that ties them together for help.)
 */

import { FINANCE_ROLES, PROCUREMENT_ROLES, USER_INVITE_ROLES } from './permissions';
import type { UserRole } from '../../types';

/** One advertised capability — a phrase the user can type and what it does. */
export interface Capability {
  /** The exact phrase to type (also shown in the help list, quoted). */
  phrase: string;
  /** One-line, user-facing description of what it does. */
  description: string;
  /** 'read' = answers a question; 'action' = creates something (a write). */
  group: 'read' | 'action';
  /** Optional access note shown as a badge, e.g. role restrictions. */
  rolesNote?: string;
  /**
   * Optional role allow-list — the SAME list the executor/read enforces. When
   * set, the help handler shows this entry only to users with one of these
   * roles, so help never advertises a capability the bot would then refuse.
   */
  roles?: readonly UserRole[];
}

/**
 * Every capability the bot advertises. Reads first, then actions (writes).
 * Order within a group is the display order in the help reply.
 */
export const CAPABILITIES: Capability[] = [
  // ── Reads (router.ts INTENTS) ──────────────────────────────────────────────
  {
    phrase: 'my open tickets',
    description: 'List the support tickets you raised that are still open.',
    group: 'read',
  },
  {
    phrase: 'overdue invoices',
    description: 'List invoices that are sent and past their due date.',
    group: 'read',
    rolesNote: 'Finance roles only',
    roles: FINANCE_ROLES,
  },
  {
    phrase: 'pending procurement',
    description: 'List purchase requests awaiting approval.',
    group: 'read',
    rolesNote: 'Admin roles only',
    roles: PROCUREMENT_ROLES,
  },
  {
    phrase: 'how do I…',
    description: 'Search the knowledge base for help articles.',
    group: 'read',
  },

  // ── Actions / writes (dialog.ts executors) ─────────────────────────────────
  {
    phrase: 'create deal',
    description: 'Add a CRM deal — title, value, stage, contact, owner.',
    group: 'action',
  },
  {
    phrase: 'create ticket',
    description: 'Open a support ticket — title, priority, category.',
    group: 'action',
  },
  {
    phrase: 'create invoice',
    description: 'Draft an invoice — client, amount, due date.',
    group: 'action',
    rolesNote: 'Finance roles only',
    roles: FINANCE_ROLES,
  },
  {
    phrase: 'create company',
    description: 'Add a CRM company — name and industry.',
    group: 'action',
  },
  {
    phrase: 'add contact',
    description: 'Add a CRM contact — name, type, company.',
    group: 'action',
  },
  {
    phrase: 'request leave',
    description: 'Request time off — type, dates, reason.',
    group: 'action',
  },
  {
    phrase: 'invite user',
    description: 'Request a new user account — an admin completes provisioning.',
    group: 'action',
    rolesNote: 'Admins only',
    roles: USER_INVITE_ROLES,
  },
];

/** Capabilities in the 'read' group, in declared order. */
export const readCapabilities = (): Capability[] => CAPABILITIES.filter(c => c.group === 'read');

/** Capabilities in the 'action' (write) group, in declared order. */
export const actionCapabilities = (): Capability[] => CAPABILITIES.filter(c => c.group === 'action');
